import BaseService from './BaseService'
import { db } from '@/config/firebase'
import { collection, query, where, getDocs, orderBy, limit, startAfter } from 'firebase/firestore'

/**
 * Service for managing channel statistics and analytics
 */
class ChannelStatisticsService extends BaseService {
  constructor() {
    super('channel_sessions')
  }

  /**
   * Get channel statistics for all channels
   * @param {string} userId - User ID to filter sessions
   * @param {string} period - Time period (7d, 30d, 90d, all)
   * @returns {Promise<Array>} Array of channel statistics
   */
  async getChannelStatistics(userId, period = '30d') {
    try {
      const channels = await this.getChannelsWithSessions(userId, period)
      return channels
    } catch (error) {
      console.error('Error getting channel statistics:', error)
      throw error
    }
  }

  /**
   * Get channels with their session statistics
   * @param {string} userId - User ID to filter sessions
   * @param {string} period - Time period
   * @returns {Promise<Array>} Array of channels with statistics
   */
  async getChannelsWithSessions(userId, period) {
    try {
      console.log('🔍 Getting channels with sessions...')
      console.log('📊 Period:', period)
      console.log('👤 User ID:', userId)
      
      // Get all channels first
      const channelsCollection = collection(db, 'channels')
      const channelsSnapshot = await getDocs(channelsCollection)
      const channels = []
      
      channelsSnapshot.forEach(doc => {
        const channelData = doc.data()
        channels.push({
          id: doc.id,
          ...channelData
        })
      })
      
      console.log('📺 Found channels:', channels.length)
      console.log('📺 Channel IDs:', channels.map(c => c.id))



      // Get session data for each channel
      const channelsWithStats = await Promise.all(
        channels.map(async (channel) => {
          console.log(`🔍 Getting stats for channel: ${channel.name} (${channel.id})`)
          const stats = await this.getChannelSessionStats(channel.id, userId, period)
          console.log(`📊 Stats for ${channel.name}:`, stats)
          return {
            ...channel,
            statistics: stats
          }
        })
      )

      console.log('✅ Final channels with stats:', channelsWithStats.length)
      return channelsWithStats
    } catch (error) {
      console.error('❌ Error getting channels with sessions:', error)
      throw error
    }
  }

  /**
   * Get session statistics for a specific channel
   * @param {string} channelId - Channel ID
   * @param {string} userId - User ID to filter sessions
   * @param {string} period - Time period
   * @returns {Promise<Object>} Channel statistics object
   */
  async getChannelSessionStats(channelId, userId, period) {
    try {
      console.log(`🔍 Getting session stats for channel: ${channelId}`)
      console.log(`📊 Period: ${period}, User ID: ${userId}`)
      
      const sessionsCollection = collection(db, 'channel_sessions')
      
      // Start with a simple query that doesn't require composite indexes
      let queryConstraints = [
        where('channelId', '==', channelId)
      ]
      
      if (userId) {
        queryConstraints.push(where('userId', '==', userId))
      }
      
      console.log(`🔍 Query constraints:`, queryConstraints)
      const sessionsQuery = query(sessionsCollection, ...queryConstraints)
      const sessionsSnapshot = await getDocs(sessionsQuery)
      
      console.log(`📊 Found ${sessionsSnapshot.size} sessions for channel ${channelId}`)
      
      const sessions = []
      sessionsSnapshot.forEach(doc => {
        const sessionData = doc.data()
        sessions.push({
          id: doc.id,
          ...sessionData
        })
      })

      // Filter by period in memory to avoid index requirements
      let filteredSessions = sessions
      if (period !== 'all') {
        const cutoffDate = this.getCutoffDate(period)
        console.log(`📅 Filtering by cutoff date: ${cutoffDate.toISOString()}`)
        filteredSessions = sessions.filter(session => {
          const startTime = session.startTime || 0
          return startTime >= cutoffDate.getTime()
        })
        console.log(`📊 After filtering: ${filteredSessions.length} sessions`)
      }

      console.log(`📊 Sample session data:`, filteredSessions.length > 0 ? filteredSessions[0] : 'No sessions')

      // Calculate statistics
      const stats = this.calculateSessionStatistics(filteredSessions)
      console.log(`📊 Calculated stats:`, stats)
      
      return stats
    } catch (error) {
      console.error('❌ Error getting channel session stats:', error)
      return {
        totalSessions: 0,
        totalWatchTime: 0,
        averageWatchTime: 0,
        uniqueDevices: 0,
        activeSessions: 0,
        lastActivity: null,
        totalErrors: 0,
        averagePingCount: 0
      }
    }
  }

  /**
   * Calculate statistics from session data
   * @param {Array} sessions - Array of session documents
   * @returns {Object} Statistics object
   */
  calculateSessionStatistics(sessions) {
    if (!sessions || sessions.length === 0) {
      return {
        totalSessions: 0,
        totalWatchTime: 0,
        averageWatchTime: 0,
        uniqueDevices: 0,
        activeSessions: 0,
        lastActivity: null,
        totalErrors: 0,
        averagePingCount: 0
      }
    }

    // Calculate total watch time using totalDuration field
    const totalWatchTime = sessions.reduce((total, session) => {
      const duration = session.totalDuration || 0
      return total + duration
    }, 0)

    // Calculate average watch time
    const averageWatchTime = totalWatchTime / sessions.length

    // Get unique devices
    const uniqueDevices = new Set(sessions.map(session => session.deviceId).filter(Boolean)).size

    // Count active sessions (sessions with status !== "ended" or recent endTime)
    const now = Date.now()
    const activeSessions = sessions.filter(session => {
      if (session.status !== "ended") return true
      // Consider session active if it ended within last 5 minutes
      return (now - session.endTime) < 300000 // 5 minutes
    }).length

    // Get last activity
    const lastActivity = sessions.reduce((latest, session) => {
      const sessionTime = session.endTime || session.startTime
      if (!latest || sessionTime > latest) {
        return sessionTime
      }
      return latest
    }, null)

    // Count sessions with errors
    const totalErrors = sessions.filter(session => session.withError === true).length

    // Calculate average ping count
    const totalPings = sessions.reduce((sum, session) => sum + (session.pingCount || 0), 0)
    const averagePingCount = sessions.length > 0 ? Math.round(totalPings / sessions.length) : 0

    return {
      totalSessions: sessions.length,
      totalWatchTime,
      averageWatchTime: Math.round(averageWatchTime),
      uniqueDevices,
      activeSessions,
      lastActivity,
      totalErrors,
      averagePingCount
    }
  }

  /**
   * Get cutoff date based on period
   * @param {string} period - Time period
   * @returns {Date} Cutoff date
   */
  getCutoffDate(period) {
    const now = new Date()
    
    switch (period) {
      case '7d':
        return new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000))
      case '30d':
        return new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000))
      case '90d':
        return new Date(now.getTime() - (90 * 24 * 60 * 60 * 1000))
      default:
        return new Date(0) // Beginning of time
    }
  }

  /**
   * Get real-time active sessions for a channel
   * @param {string} channelId - Channel ID
   * @returns {Promise<Array>} Array of active sessions
   */
  async getActiveSessions(channelId) {
    try {
      const sessionsCollection = collection(db, 'channel_sessions')
      const now = new Date()
      const fiveMinutesAgo = new Date(now.getTime() - 300000) // 5 minutes ago
      
      const activeQuery = query(
        sessionsCollection,
        where('channelId', '==', channelId),
        where('startTime', '>=', fiveMinutesAgo)
      )
      
      const snapshot = await getDocs(activeQuery)
      const activeSessions = []
      
      snapshot.forEach(doc => {
        const sessionData = doc.data()
        activeSessions.push({
          id: doc.id,
          ...sessionData
        })
      })
      
      return activeSessions
    } catch (error) {
      console.error('Error getting active sessions:', error)
      return []
    }
  }

  /**
   * Get device usage statistics for a channel
   * @param {string} channelId - Channel ID
   * @param {string} period - Time period
   * @returns {Promise<Object>} Device usage statistics
   */
  async getDeviceUsageStats(channelId, period = '30d') {
    try {
      console.log(`🔍 Getting device usage stats for channel: ${channelId}, period: ${period}`)
      
      const stats = await this.getChannelSessionStats(channelId, null, period)
      console.log(`📊 Base stats:`, stats)
      
      // Get detailed device information
      const sessionsCollection = collection(db, 'channel_sessions')
      const cutoffDate = this.getCutoffDate(period)
      console.log(`📅 Cutoff date: ${cutoffDate.toISOString()}`)
      
      // Use the same approach as getChannelSessionStats - avoid composite queries
      const deviceQuery = query(
        sessionsCollection,
        where('channelId', '==', channelId)
      )
      
      const snapshot = await getDocs(deviceQuery)
      console.log(`📊 Found ${snapshot.size} total sessions for device details`)
      
      const deviceStats = {}
      
      snapshot.forEach(doc => {
        const sessionData = doc.data()
        const deviceId = sessionData.deviceId
        
        // Filter by period in memory
        const startTime = sessionData.startTime || 0
        if (period !== 'all' && startTime < cutoffDate.getTime()) {
          return // Skip this session if it's outside the period
        }
        
        if (!deviceStats[deviceId]) {
          deviceStats[deviceId] = {
            deviceId,
            totalSessions: 0,
            totalWatchTime: 0,
            lastSeen: null
          }
        }
        
        deviceStats[deviceId].totalSessions++
        deviceStats[deviceId].totalWatchTime += sessionData.totalDuration || 0
        
        const sessionTime = sessionData.endTime || sessionData.startTime
        if (!deviceStats[deviceId].lastSeen || sessionTime > deviceStats[deviceId].lastSeen) {
          deviceStats[deviceId].lastSeen = sessionTime
        }
      })
      
      console.log(`📊 Device stats:`, deviceStats)
      console.log(`📊 Device details count:`, Object.keys(deviceStats).length)
      
      const result = {
        ...stats,
        deviceDetails: Object.values(deviceStats)
      }
      
      console.log(`📊 Final result:`, result)
      return result
      
    } catch (error) {
      console.error('❌ Error getting device usage stats:', error)
      return {
        totalSessions: 0,
        totalWatchTime: 0,
        averageWatchTime: 0,
        uniqueDevices: 0,
        activeSessions: 0,
        lastActivity: null,
        totalErrors: 0,
        averagePingCount: 0,
        deviceDetails: []
      }
    }
  }

  /**
   * Format watch time for display
   * @param {number} milliseconds - Time in milliseconds
   * @returns {string} Formatted time string
   */
  formatWatchTime(milliseconds) {
    if (!milliseconds || milliseconds === 0) return '0s'
    
    const seconds = Math.floor(milliseconds / 1000)
    const minutes = Math.floor(seconds / 60)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)
    
    if (days > 0) {
      return `${days}d ${hours % 24}h ${minutes % 60}m`
    } else if (hours > 0) {
      return `${hours}h ${minutes % 60}m`
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    } else {
      return `${seconds}s`
    }
  }

  /**
   * Format date for display
   * @param {Date|number} date - Date to format
   * @returns {string} Formatted date string
   */
  formatDate(date) {
    if (!date) return 'Never'
    
    const dateObj = new Date(date)
    const now = new Date()
    const diffTime = Math.abs(now - dateObj)
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    if (diffDays === 1) {
      return 'Today'
    } else if (diffDays === 2) {
      return 'Yesterday'
    } else if (diffDays <= 7) {
      return `${diffDays - 1} days ago`
    } else {
      return dateObj.toLocaleDateString()
    }
  }

}

export default new ChannelStatisticsService()
